import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, Sparkles, Plus } from "lucide-react";
import { CVD_PAIRS } from "@/data/content";

// The specific detail points Ascendia adds beyond ordinary cleaning, per area.
const EXTRAS = {
  "Window Sill & Track": ["Tracks", "Frame", "Corners", "Hidden build-up"],
  "Sink & Faucet": ["Faucet base", "Drain edge", "Crevices", "Limescale"],
  "Bathroom": ["Grout", "Fixtures", "Drains", "Hinges", "Steam detail"],
  "Cabinets": ["Interiors", "Hinges", "Runners", "Edges"],
  "Doors": ["Tops", "Frames", "Handles", "Edges"],
  "Kitchen": ["Backsplash", "Appliance exteriors", "Kickboards", "Sink crevices"],
};

// One-line "why it matters" note per detail point (tap a chip to reveal).
const NOTES = {
  "Tracks": "Grit in the runner is what makes windows stick — we clear it out.",
  "Frame": "Frames hold a grey film most cleans skip.",
  "Corners": "Where the sill meets the frame collects the worst build-up.",
  "Hidden build-up": "The dirt you only see once the light hits it.",
  "Faucet base": "Limescale rings the base — we take it back to the metal.",
  "Drain edge": "The lip around the drain traps residue and smell.",
  "Crevices": "Tight seams a cloth can't reach on a normal clean.",
  "Limescale": "Hard-water scale, removed rather than wiped over.",
  "Grout": "Lines between tiles are detailed, not just surface-mopped.",
  "Fixtures": "Taps, handles and shower fittings polished, not left spotted.",
  "Drains": "Visible drains and surrounds cleared of residue.",
  "Hinges": "Greasy dust in the pivots removed so they move clean.",
  "Steam detail": "A steam pass on stubborn build-up where needed.",
  "Interiors": "Inside the cabinet, not just the doors.",
  "Runners": "Drawer sliders cleared so they run smooth.",
  "Edges": "The edges and undersides people forget.",
  "Tops": "The top of the door — the one surface nobody checks.",
  "Frames": "Door frames wiped edge to edge.",
  "Handles": "High-touch handles cleaned and polished.",
  "Backsplash": "Splatter behind the hob and sink lifted off.",
  "Appliance exteriors": "Fronts and sides detailed, fingerprints gone.",
  "Kickboards": "The low panels under units, wiped down.",
  "Sink crevices": "The seam and overflow around the sink.",
};

export const CleaningVsDetailing = () => {
  const [mode, setMode] = useState("detailing");
  const [openChip, setOpenChip] = useState(null); // `${area}::${extra}`
  const reduce = useReducedMotion();
  const isDetail = mode === "detailing";
  return (
    <div data-testid="cleaning-vs-detailing">
      <div className="flex justify-center">
        <div className="relative inline-flex rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.03] p-1">
          {[["cleaning", "Professional Cleaning"], ["detailing", "Ascendia Detailing"]].map(([key, label]) => {
            const activeMode = mode === key;
            return (
              <button key={key} data-testid={`cvd-toggle-${key}`} onClick={() => { setMode(key); setOpenChip(null); }}
                className={`relative rounded-lg px-5 py-2 text-sm font-medium transition-colors duration-150 ${activeMode ? (key === "detailing" ? "text-black" : "text-white") : "text-white/60 hover:text-white"}`}>
                {activeMode && (
                  <motion.span layoutId={reduce ? undefined : "cvdPill"} className={`absolute inset-0 rounded-lg ${key === "detailing" ? "bg-gold" : "bg-white/10"}`} transition={{ type: "spring", stiffness: 380, damping: 32 }} />
                )}
                <span className="relative">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CVD_PAIRS.map((p) => {
          const chips = EXTRAS[p.area] || [];
          const openHere = openChip && openChip.startsWith(`${p.area}::`);
          const openExtra = openHere ? openChip.split("::")[1] : null;
          return (
            <motion.div key={p.area} layout className="ascendia-card p-5" animate={{ borderColor: isDetail ? "var(--border-strong)" : "var(--border-subtle)" }} transition={{ duration: reduce ? 0 : 0.2 }}>
              <div className="flex items-center gap-2">
                {isDetail ? <Sparkles className="h-4 w-4 text-gold" /> : <Check className="h-4 w-4 text-white/50" />}
                <h4 className="font-display font-semibold text-white">{p.area}</h4>
              </div>
              <div className="relative mt-3 min-h-[3.5rem]">
                <AnimatePresence mode="wait">
                  <motion.p key={mode}
                    initial={reduce ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: reduce ? 0 : 0.18 }}
                    className={`text-sm ${isDetail ? "text-secondary" : "text-muted-c"}`}>
                    {isDetail ? p.detailing : p.cleaning}
                  </motion.p>
                </AnimatePresence>
              </div>
              <AnimatePresence>
                {isDetail && (
                  <motion.div
                    initial={reduce ? false : { opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: reduce ? 0 : 0.24 }} className="overflow-hidden">
                    <p className="mt-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-gold-soft">Ascendia adds · tap to see why</p>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map((x, i) => {
                        const chipKey = `${p.area}::${x}`;
                        const on = openChip === chipKey;
                        return (
                          <motion.button key={x} type="button"
                            onClick={() => setOpenChip((prev) => (prev === chipKey ? null : chipKey))}
                            data-testid={`cvd-hotspot-${p.area}-${x}`.replace(/\s+/g, "-").toLowerCase()}
                            aria-pressed={on}
                            initial={reduce ? false : { opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: reduce ? 0 : 0.06 * i, duration: 0.15 }}
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${on ? "border-[color:var(--border-strong)] bg-gold/20 text-gold-soft" : "border-[color:var(--border-strong)] bg-gold/[0.06] text-gold-soft hover:bg-gold/[0.12]"}`}>
                            <Plus className="h-3 w-3" /> {x}
                          </motion.button>
                        );
                      })}
                    </div>
                    <AnimatePresence mode="wait">
                      {openHere && (
                        <motion.p key={openExtra}
                          initial={reduce ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          transition={{ duration: reduce ? 0 : 0.16 }}
                          className="mt-3 rounded-lg border border-[color:var(--border-subtle)] bg-black/30 p-2.5 text-xs text-secondary"
                          data-testid="cvd-hotspot-note">
                          {NOTES[openExtra] || "A detail point most cleans skip."}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      <p className="mt-6 text-center text-sm text-muted-c">{isDetail ? "Detailing works every surface — including the parts most people never see." : "Professional cleaning keeps a property presentable and move-in ready."}</p>
    </div>
  );
};

export default CleaningVsDetailing;
